import { z } from 'zod';
import { CREDIT_SCALE } from './credits.js';

// ─────────────────────────────────────────────────────────────────
// Billing & credits — shared with the editor UI
//
// Credits are stored as integer micro-credits (1 credit = CREDIT_SCALE
// micro-credits) so pricing math never touches floats. Amounts in the
// money tables (invoices, packages) are the smallest currency unit
// (kobo for NGN), also integers.
// ─────────────────────────────────────────────────────────────────

export const CreditTransactionTypeSchema = z.enum([
  'purchase', // credits bought via invoice
  'usage', // credits consumed by an AI request/job
  'grant', // free/promotional credits (e.g. signup bonus)
  'refund', // credits returned (failed job, dispute, etc.)
  'adjustment', // manual admin correction
  'expiration', // credits expired unused
]);
export type CreditTransactionType = z.infer<typeof CreditTransactionTypeSchema>;

/** What an invoice bills for: one-time credit top-ups vs recurring plan charges. */
export const InvoiceKindSchema = z.enum(['credits', 'subscription']);
export type InvoiceKind = z.infer<typeof InvoiceKindSchema>;

export function microCreditsToCredits(microCredits: number): number {
  return microCredits / CREDIT_SCALE;
}

// ── Credit balance ───────────────────────────────────────────────

export const CreditBalanceInfoSchema = z.object({
  workspaceId: z.string().uuid(),
  /** Integer micro-credits currently available. */
  microCredits: z.number().int(),
  /** microCredits / CREDIT_SCALE — for display only. */
  credits: z.number(),
  updatedAt: z.string().nullable(),
});
export type CreditBalanceInfo = z.infer<typeof CreditBalanceInfoSchema>;

// ── Ledger entries ───────────────────────────────────────────────

export const BillingTransactionSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  type: CreditTransactionTypeSchema,
  /** Signed micro-credits: positive = added, negative = consumed. */
  amountMicroCredits: z.number().int(),
  /** Balance snapshot immediately after this entry. */
  balanceAfterMicroCredits: z.number().int(),
  currency: z.string().nullable(),
  description: z.string().nullable(),
  resourceType: z.string().nullable(),
  invoiceId: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type BillingTransaction = z.infer<typeof BillingTransactionSchema>;

export const BillingTransactionsResultSchema = z.object({
  data: z.array(BillingTransactionSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type BillingTransactionsResult = z.infer<typeof BillingTransactionsResultSchema>;

// ── Overview ─────────────────────────────────────────────────────

export const SubscriptionSummarySchema = z.object({
  plan: z.string(),
  status: z.string(),
  quantity: z.number().int().nullable(),
  currentPeriodEnd: z.string().nullable(),
  provider: z.string().nullable(),
});
export type SubscriptionSummary = z.infer<typeof SubscriptionSummarySchema>;

export const BillingOverviewSchema = z.object({
  workspace: z.object({
    id: z.string().uuid(),
    name: z.string(),
    role: z.enum(['owner', 'admin', 'member']),
  }),
  balance: CreditBalanceInfoSchema.nullable(),
  /** Active/trialing subscription when one exists (null until plan tiers land). */
  subscription: SubscriptionSummarySchema.nullable(),
  recentTransactions: z.array(BillingTransactionSchema),
});
export type BillingOverview = z.infer<typeof BillingOverviewSchema>;

// ── Credit packages (what users buy) ─────────────────────────────

export const CreditPackageSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Number of whole credits granted. */
  credits: z.number().int(),
  /** Price in the smallest currency unit (kobo for NGN). */
  priceAmount: z.number().int(),
  currency: z.string(),
});
export type CreditPackage = z.infer<typeof CreditPackageSchema>;

export const CreditPackagesResultSchema = z.object({
  packages: z.array(CreditPackageSchema),
});
export type CreditPackagesResult = z.infer<typeof CreditPackagesResultSchema>;

// ── Purchases (Paystack hosted checkout) ─────────────────────────

export const PurchaseCheckoutRequestSchema = z.object({
  packageId: z.string().uuid(),
  /** Where Paystack sends the customer after payment; defaults to the caller origin + /billing/return. */
  callbackUrl: z.string().url().optional(),
});
export type PurchaseCheckoutRequest = z.infer<typeof PurchaseCheckoutRequestSchema>;

export const PurchaseCheckoutResultSchema = z.object({
  invoiceId: z.string().uuid(),
  /** Our reference — also the Paystack transaction reference (invoice.providerRef). */
  reference: z.string(),
  /** Paystack-hosted checkout to redirect the customer to. */
  authorizationUrl: z.string(),
});
export type PurchaseCheckoutResult = z.infer<typeof PurchaseCheckoutResultSchema>;

export const VerifyPurchaseRequestSchema = z.object({
  reference: z.string().min(1),
});
export type VerifyPurchaseRequest = z.infer<typeof VerifyPurchaseRequestSchema>;

export const VerifyPurchaseResultSchema = z.object({
  invoiceId: z.string().uuid().nullable(),
  status: z.enum(['paid', 'already_paid', 'pending', 'failed', 'not_found']),
  creditsAddedMicro: z.number().int().nullable(),
  balanceAfterMicro: z.number().int().nullable(),
});
export type VerifyPurchaseResult = z.infer<typeof VerifyPurchaseResultSchema>;

// ── Invoices (purchase history) ──────────────────────────────────

export const BillingInvoiceSchema = z.object({
  id: z.string().uuid(),
  kind: InvoiceKindSchema,
  status: z.enum(['draft', 'open', 'paid', 'void', 'uncollectible']),
  amountDue: z.number().int(),
  currency: z.string(),
  /** Present (and > 0) for credit-package invoices; null for subscription charges. */
  creditAmountMicro: z.number().int().nullable(),
  /** Term paid for, in days (subscription invoices); null for credit top-ups. */
  periodDays: z.number().int().nullable(),
  providerRef: z.string().nullable(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
});
export type BillingInvoice = z.infer<typeof BillingInvoiceSchema>;

export const BillingInvoicesResultSchema = z.object({
  data: z.array(BillingInvoiceSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type BillingInvoicesResult = z.infer<typeof BillingInvoicesResultSchema>;

// ── Subscription plan catalog ────────────────────────────────────
// Plan tiers are a marketing/config concern distinct from credit
// packages (which top up the metered AI wallet). Recurring
// subscription purchasing is not wired yet — the Plans page uses this
// catalog read-only while subscriptions stay dormant in the DB.

export const SubscriptionPlanIdSchema = z.enum(['free', 'individual', 'pro', 'enterprise']);
export type SubscriptionPlanId = z.infer<typeof SubscriptionPlanIdSchema>;

export const PlanTierSchema = z.object({
  id: SubscriptionPlanIdSchema,
  name: z.string(),
  description: z.string(),
  /** Monthly price in the smallest currency unit (0 for free / custom tiers). */
  monthlyPriceMinor: z.number().int(),
  currency: z.string(),
  features: z.array(z.string()),
  highlighted: z.boolean().optional(),
});
export type PlanTier = z.infer<typeof PlanTierSchema>;

/** Static tier catalog (NGN, kobo amounts) until plan management lands. */
export const SUBSCRIPTION_PLANS: PlanTier[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Everything you need to try BlogForger.',
    monthlyPriceMinor: 0,
    currency: 'NGN',
    features: ['3 published posts', 'AI drafting (metered credits)', 'Community support'],
  },
  {
    id: 'individual',
    name: 'Individual',
    description: 'For solo creators and side projects.',
    monthlyPriceMinor: 500_000, // ₦5,000
    currency: 'NGN',
    features: ['Unlimited posts & emails', 'Advanced components', 'Email support'],
    highlighted: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For growing teams and publishers.',
    monthlyPriceMinor: 1_500_000, // ₦15,000
    currency: 'NGN',
    features: ['Everything in Individual', 'Multiple workspaces', 'Priority support'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Custom limits, SSO and a dedicated success manager.',
    monthlyPriceMinor: 0, // custom — contact sales
    currency: 'NGN',
    features: ['Everything in Pro', 'SSO / SAML', 'Dedicated support'],
  },
];

/** Trial availability computed for the acting owner (subscriptions are workspace-scoped). */
export const TrialAvailabilitySchema = z.object({
  available: z.boolean(),
  days: z.number().int(),
});
export type TrialAvailability = z.infer<typeof TrialAvailabilitySchema>;

export const PlansResultSchema = z.object({
  /** The workspace's current active/trialing subscription row, when one exists. */
  current: SubscriptionSummarySchema.nullable(),
  /** Full tier catalog (marketing/config). */
  plans: z.array(PlanTierSchema),
  /** Term (in days) one payment buys. */
  termDays: z.number().int(),
  /** Whether the acting user is the workspace owner and may manage the plan. */
  canManage: z.boolean(),
  /** Trial availability for the acting owner (null for non-owners / when already on a plan). */
  trial: TrialAvailabilitySchema.nullable(),
});
export type PlansResult = z.infer<typeof PlansResultSchema>;

// ── Subscription purchasing (no auto-renew) ──────────────────────
// Each paid period is a fresh one-time Paystack checkout of the tier's
// monthly price; the term length is snapshotted on the invoice
// (period_days) so renewal maths never re-reads config. Trials are a
// separate, free activation limited to one per workspace AND one per
// owner user (users.trial_used_at).

/** Only 'individual' is purchasable today. */
export const PurchasablePlanIdSchema = z.literal('individual');
export type PurchasablePlanId = z.infer<typeof PurchasablePlanIdSchema>;

export const StartTrialRequestSchema = z.object({});
export type StartTrialRequest = z.infer<typeof StartTrialRequestSchema>;

export const StartTrialResultSchema = z.object({
  subscription: SubscriptionSummarySchema,
});
export type StartTrialResult = z.infer<typeof StartTrialResultSchema>;

export const SubscriptionCheckoutRequestSchema = z.object({
  planId: PurchasablePlanIdSchema,
  /** Where Paystack sends the customer after payment; defaults to the caller origin + /billing/return. */
  callbackUrl: z.string().url().optional(),
});
export type SubscriptionCheckoutRequest = z.infer<typeof SubscriptionCheckoutRequestSchema>;

export const SubscriptionCheckoutResultSchema = z.object({
  subscriptionId: z.string().uuid(),
  invoiceId: z.string().uuid(),
  /** Our reference — also the Paystack transaction reference (invoice.providerRef). */
  reference: z.string(),
  authorizationUrl: z.string(),
});
export type SubscriptionCheckoutResult = z.infer<typeof SubscriptionCheckoutResultSchema>;
